import { Point } from "highcharts";
import Highcharts from "highcharts/highstock";
import { isNil } from "lodash";
import React, { useMemo, useRef } from "react";
import "../insHorizontalBarDatapointChart.scss";
import { renderToStaticMarkup } from "react-dom/server";
import { useDispatch } from "react-redux";
import { InsLabelValueTooltip } from "../../../base/common/insLabelValueTooltip";
import { defaultMargin } from "../constants";
import { dataLabelFormatter, yAxisFormatter } from "../ins-horizontal-bar-chart-formatter";
import { ManageScroll } from "../ins-horizontal-bar-chart-scrolling";
import { onRenderHorizontalBarChart, useCalculateDesign } from "../ins-horizontal-bar-datapoint-chart-helper";
import { formatToolTip } from "./ins-horizontal-stacked-bar-chart-adapters";
import { horizontalStackedBarChartMapper } from "./ins-horizontal-stacked-bar-chart-helpers";
import { InsHighcharts } from "components/widgets/base/insHighcharts";
import { InsQueryStatedElement } from "components/widgets/base/insQueryStatedElement";
import { useCurrentWidgetState, useMasterDetailsChoice, useShownValues, useWidgetChildrenId, usePatchedWidgetComponentProps, useWidgetCurrentDisplayOptions, useWidgetCurrentSortBy } from "components/widgets/hooks/use-current-widget";
import { useGetSelectedCurrency } from "components/workspaces/hooks/use-current-dashboard";
import { useGetDatapointsWidgetData } from "hooks/widget-data/use-datapoints";
import { applyUserSettings } from "hooks/widget-data/user-settings/user-settings";
import { setCurrentStatusMasterDetails } from "store/current-dashboard";
Highcharts.wrap(Highcharts.Chart.prototype, "render", ManageScroll);
// eslint-disable-next-line max-lines-per-function
export const InsHorizontalDatapointStackedBarChart = (props) => {
    const { sectionId, widgetId } = props, childrenId = useWidgetChildrenId(sectionId, widgetId), dispatch = useDispatch(), displayOptions = useWidgetCurrentDisplayOptions(sectionId, widgetId), masterDetailChoice = useMasterDetailsChoice(sectionId, widgetId), selectedCurrency = useGetSelectedCurrency(), shownValues = useShownValues(sectionId, widgetId), sortedBy = useWidgetCurrentSortBy(sectionId, widgetId), widgetProps = usePatchedWidgetComponentProps(sectionId, widgetId), widgetState = useCurrentWidgetState(sectionId, widgetId);
    const displayValueType = widgetState?.displayValueType, selectedValues = widgetState?.selectedValues ?? [];
    const { showScale, showTotals } = displayOptions ?? {};
    const selectedDropDownProps = Object.entries(selectedValues).map(([key, value]) => widgetProps.dataSelectProps[parseInt(key, 10)].selectValues[value]);
    const displayKind = selectedDropDownProps[0]?.valueFormat ?? widgetProps.valueFormat;
    const [[data], queryState, refresh] = useGetDatapointsWidgetData(props);
    const chartContainerRef = useRef(null), chartRef = useRef(null);
    const masterDetailChoiceIndex = useRef(0);
    const categories = useRef([]);
    if (data && data.data && data.data[0]) {
        categories.current = data.data[0]?.metaData?.categories;
    }
    const options = useMemo(
    // eslint-disable-next-line max-lines-per-function
    () => {
        let includesNegative = false, max = 0, min = 0, sum = 0;
        let series = [];
        if (data && data.data && data.data[0]) {
            max = data.data[0].metaData?.max;
            min = data.data[0].metaData?.min;
            sum = data.data[0].metaData?.sum;
            includesNegative = min < 0;
            if (widgetProps.childrenId && !masterDetailChoice) {
                // Set default master details value
                dispatch(setCurrentStatusMasterDetails({ childrenId,
                    sectionId: props.sectionId,
                    selectedValue: data.data[0].metaData?.masterDetailsChoice,
                    widgetId: props.widgetId }));
            }
            series = data.data.map((record, index) => {
                const pointOptionsObjects = record.data;
                const pointsWithSettings = applyUserSettings(pointOptionsObjects, { shownValues, sortBy: sortedBy }, false, null, false, horizontalStackedBarChartMapper);
                if (pointsWithSettings) {
                    categories.current = categories.current.filter((c) => pointsWithSettings.some((p) => p.name === c));
                }
                return {
                    colorIndex: record.metaData?.colorIndex ?? index,
                    data: pointsWithSettings,
                    dataLabels: {
                        align: "left",
                        crop: false,
                        enabled: false,
                        formatter() {
                            return dataLabelFormatter(this, displayKind, displayValueType);
                        },
                        inside: false,
                        overflow: "justify"
                    },
                    dataSorting: {
                        enabled: false
                    },
                    events: { click(event) {
                            if (widgetProps.childrenId) {
                                dispatch(setCurrentStatusMasterDetails({ childrenId, sectionId: props.sectionId, selectedValue: event.point.name, widgetId: props.widgetId }));
                            }
                        } },
                    id: childrenId ? `${record.name}_${index}` : record.name,
                    name: record.name,
                    showInLegend: record?.data?.some((val) => val?.y !== 0) || false,
                    type: "bar"
                };
            });
            if (widgetProps.childrenId && series[0] && series[0].data) {
                masterDetailChoiceIndex.current = series[0].data.findIndex((val) => val.name === masterDetailChoice) ?? 0;
            }
        }
        return {
            chart: {
                className: "default-horizontal-bar-char",
                events: {
                    render() {
                        onRenderHorizontalBarChart(this, widgetProps, dispatch, sectionId, widgetId);
                    }
                },
                marginLeft: defaultMargin,
                marginRight: 15,
                plotShadow: false,
                styledMode: true,
                type: "bar"
            },
            credits: {
                enabled: false
            },
            legend: {
                enabled: true,
                symbolRadius: 0
            },
            plotOptions: {
                animation: false,
                bar: {
                    dataLabels: {
                        enabled: true
                    }
                },
                series: {
                    stacking: widgetProps?.unstack ? undefined : "normal"
                }
            },
            series,
            title: {
                text: ""
            },
            tooltip: {
                enabled: true,
                formatter() {
                    const labelValuePairs = formatToolTip(widgetProps, this, selectedDropDownProps, displayKind, selectedCurrency, sum, displayValueType);
                    return renderToStaticMarkup(React.createElement(InsLabelValueTooltip, { labelValuePairs: labelValuePairs }));
                }
            },
            xAxis: {
                gridLineWidth: !isNil(showScale) && showScale === false ? 0 : 2,
                labels: {
                    allowOverlap: false,
                    useHTML: true
                },
                min: 0,
                type: "category"
            },
            yAxis: {
                labels: {
                    allowOverlap: false,
                    enabled: isNil(showScale) || showScale,
                    formatter() {
                        return yAxisFormatter(this, sum, "Sum", includesNegative, displayValueType);
                    },
                    useHTML: true
                },
                max,
                min,
                plotLines: [{ value: 0 }],
                stackLabels: {
                    allowOverlap: false,
                    crop: false,
                    enabled: showTotals === true,
                    formatter() {
                        const pointObject = {
                            y: this.total
                        };
                        pointObject.point = new Point();
                        pointObject.point.options = { custom: {
                                percentageOfTotal: sum && sum !== 0 ? this.total / sum : 0
                            } };
                        return dataLabelFormatter(pointObject, displayKind, displayValueType);
                    },
                    overflow: "allow"
                },
                title: {
                    text: "",
                    useHTML: true
                },
                visible: true
            }
        };
    }, [
        data,
        masterDetailChoice,
        showScale,
        showTotals,
        shownValues,
        sortedBy,
        displayKind,
        displayValueType,
        widgetProps,
        dispatch,
        childrenId,
        props.sectionId,
        props.widgetId,
        sectionId,
        widgetId,
        selectedDropDownProps,
        selectedCurrency
    ]);
    useCalculateDesign({ categories: categories.current,
        chartContainerRef,
        chartRef,
        childrenId: widgetProps.childrenId,
        data: data?.data,
        dataLabelsEnabled: showTotals || false,
        displayValueType,
        masterDetailChoiceIndex: masterDetailChoiceIndex.current,
        noDefineLabel: false,
        options,
        preformatted: false,
        valueFormat: displayKind });
    return React.createElement(InsQueryStatedElement, { widgetFactoryProps: props, queryState: queryState, hasData: Boolean(data?.data?.length > 0) },
        React.createElement(InsHighcharts, { key: `${shownValues.filter}-${shownValues.quantity}-${sortedBy}`, options: options, ref: chartRef, wrapperRef: chartContainerRef, containerClassName: showScale === false ? "scaleless-horizontal-bar-chart" : "default-horizontal-bar-chart", colorSet: widgetProps.colorSet, sectionId: sectionId, widgetId: widgetId, isMaximizedOne: props.isMaximizedOne, refreshAction: refresh }));
};
